import { Component, Input, SimpleChanges } from '@angular/core';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
@Component({
  selector: 'app-common-bulk-print',
  templateUrl: './common-bulk-print.component.html',
  styleUrls: ['./common-bulk-print.component.css']
})
export class CommonBulkPrintComponent {
  @Input() columns: any[] = []; // Table columns definition
  @Input() data: any[] = []; // Data to be displayed
  @Input() downloadPdf: boolean = true;
  @Input() print: boolean = true;
  @Input() columnsToExport: any[] = [];
  @Input() fileName: string = '';
  @Input() allRecords: any;
  @Input() exportData: any[] = [];
  @Input() customHeaders: { label: string; startCol: number; endCol: number }[] = [];

  columnHeaderMap: { [identifier: string]: string } = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.columnHeaderMap = this.columns.reduce((acc, col) => {
        const identifier = col.id ?? col.key;
        acc[identifier] = col.header;
        return acc;
      }, {} as { [key: string]: string });
    }
  }
  
  getFilteredColumns(): any[] {
    if (!this.columnsToExport || this.columnsToExport.length === 0) {
      return this.columns;
    }
    return this.columns.filter(col => 
      this.columnsToExport.includes(col.id ?? col.key)
    );
  }

  private getApprovalStatusName(status: number | undefined): string {
    switch (status) {
      case 1: return 'Pending';
      case 2: return 'Approved';
      case 9: return 'Partially Approved';
      case 3: return 'Rejected';
      case 4: return 'Commented';
      default: return '';
    }
  }
  
  // Utility to extract values (handles nested data)
  getValue(key: string, record: any): any {
    if (!record || !key) return '';
    
    const value = record[key];
    if (value === null || value === undefined) return '';

    switch (key) {
      case 'expenseAssociated':
        return value.map((v: any) => v.expense).filter(Boolean).join(', ');
      case 'invoiceLineItems':
        return value.map((v: any) => v.expense).filter(Boolean).join(', ');
      case 'invoiceLineItems':
        return value.map((v: any) => v.expense).filter(Boolean).join(', ');
      case 'venderInvoiceNumber':
        return value.map((v: any) => v.invoiceNumber).filter(Boolean).join(', ');
      case 'expenseModeMapping':
        return value.map((v: any) => v.expenseMode).filter(Boolean).join(', ');
      case 'approvalUser':
        return value.map((v: any) => `${v.name || ''} ${v.approvalStatus ? `(${v.approvalStatus})` : ''}`).filter(Boolean).join(', ');
        case 'allApprovalsUser':
          return value
            .map((v: any) => {
              const statusName = this.getApprovalStatusName(v.approvalStatus);
              const dateFormatted = v.updatedAt
                ? new Date(v.updatedAt).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : '';
    
              let approvalPart = '';
              if (v.approvalStatus === 2) {
                approvalPart = ` (${statusName}) - ${dateFormatted}`;
              }
    
              const remarksPart = v?.remarks ? ` - ${v.remarks}` : '';
              return `(${v?.sequence}) ${v.name || ''}${approvalPart}${remarksPart}`.trim();
            })
            .filter(Boolean)
            .join(', ');

        case 'attributes':
        return value.map((v: any) => v.quickCodeName).filter(Boolean).join(', ');
      case 'approvalHierarchy':
        return value.map((v: any) => {
          const des = v.designation || '';
          const cond = v.condition ? `(${v.condition})` : '';
          return `${des} ${cond}`.trim();
        }).filter(Boolean).join(', ');
      case 'userRole':
        return value.map((v: any) => v.roleDescription).filter(Boolean).join(', ');
      case 'warehouseList':
        return value.map((v: any) => {
          const client = v.clientName || '';
          const wh = v.warehouseName || '';
          return client && wh ? `${client} - ${wh}` : client || wh;
        }).filter(Boolean).join(', ');
      default:
        return Array.isArray(value)
          ? value.join(', ')
          : typeof value === 'object'
          ? JSON.stringify(value)
          : value;
    }
  }

  // Generate printable HTML content
  generatePrintContent(columns: any[], data: any[]): string {
    let content = `<html><head>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
        th { background-color: #f0f0f0; font-weight: bold; width: 200px },
        td { width: calc(100% - 200px); }
        .custom-header { background-color: #e0e0e0; font-weight: bold; text-align: center; }
      </style>
    </head><body>`;

    // Add custom headers if provided
    if (this.customHeaders && this.customHeaders.length > 0) {
      content += `<table><tbody><tr>`;
      const headerRow = new Array(columns.length).fill('');
      this.customHeaders.forEach(header => {
        headerRow[header.startCol - 1] = `<th colspan="${header.endCol - header.startCol + 1}" class="custom-header">${header.label}</th>`;
      });
      content += headerRow.filter(val => val).join('') + `</tr>`;
      // Add column headers
      content += `<tr>${columns.map(col => `<th>${col.header}</th>`).join('')}</tr>`;
      content += `</tbody></table>`;
    }

    data.forEach(record => {
      content += `<table><tbody>`;
      columns.forEach(col => {
        const val = this.getValue(col.key, record);
        content += `<tr><th>${col.header}</th><td>${val}</td></tr>`;
      });
      content += `</tbody></table>`;
    });

    content += `</body></html>`;
    return content;
  }

  printRecords(): void {
    const columns = this.getFilteredColumns();
    const records = this.exportData?.length ? this.exportData : this.data;

    if (!records?.length) {
      console.error('No records available to print.');
      return;
    }

    const printContent = this.generatePrintContent(columns, records);
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(printContent);
      win.document.close();
      win.focus();
      win.print();
    } else {
      console.error('Unable to open print window.');
    }
  }

  // PDF Download (supports bulk)
  downloadPDF(): void {
    const columns = this.getFilteredColumns();
    const records = this.exportData?.length ? this.exportData : this.data;

    if (!records?.length) {
      console.error('No records available for PDF export.');
      return;
    }

    const doc = new jsPDF();
    const exportIdentifiers = columns.map(col => col.id ?? col.key);
    const headers = exportIdentifiers.map(id => this.columnHeaderMap[id] || id.toString());

    const processedData = records.map(record => {
      return columns.map(col => this.getValue(col.key, record));
    });

    const headerRows = this.customHeaders && this.customHeaders.length > 0
      ? [
          this.customHeaders.map(header => ({
            content: header.label,
            colSpan: header.endCol - header.startCol + 1,
            styles: { halign: 'center', fontStyle: 'bold' }
          })),
          headers
        ]
      : [headers];

    (doc as any).autoTable({
      head: headerRows,
      body: processedData,
      startY: 20,
      theme: 'grid',
      styles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 50 } },
      didDrawPage: (data: any) => {
        doc.text(this.fileName || 'Export', 14, 10);
      }
    });

    doc.save(this.fileName || 'export.pdf');
  }
}
